import React, { useState } from 'react'
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  Stack,
  Typography,
  Box,
  Divider,
  Paper,
  Grid,
  Chip as MuiChip,
  Button,
} from '@mui/material'
import DateFormatter from '@/utilities/date-formatter'
import { AreaControllerData } from '@/utilities/types/table'

interface AreaControllerCollumnChipsProps {
  userAreaControllers: AreaControllerData[]
}

const AreaControllerCollumnChips: React.FC<AreaControllerCollumnChipsProps> = ({
  userAreaControllers,
}) => {
  const [selectedController, setSelectedController] =
    useState<AreaControllerData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false)

  const handleChipClick = (controller: AreaControllerData) => {
    setSelectedController(controller)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedController(null)
  }

  const handleBackToList = () => {
    setIsModalOpen(false)
    setSelectedController(null)
    setIsViewAllModalOpen(true)
  }

  const handleViewAllClick = () => {
    setIsViewAllModalOpen(true)
  }

  if (!userAreaControllers || userAreaControllers.length === 0) {
    return <span>-</span>
  }

  const renderChips = () => {
    if (userAreaControllers.length <= 1) {
      return userAreaControllers.map((controller) => (
        <Chip
          key={controller.id}
          label={`${controller.firstName} ${controller.lastName}`}
          onClick={() => handleChipClick(controller)}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.08)',
            },
            margin: '2px',
          }}
          variant="outlined"
          color="primary"
        />
      ))
    }

    return (
      <Chip
        label={`View All AC (${userAreaControllers.length})`}
        onClick={handleViewAllClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
          },
        }}
        variant="outlined"
        color="primary"
      />
    )
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {renderChips()}
      </Stack>

      {/* Individual AC Detail Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        {selectedController && (
          <>
            <DialogTitle
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {userAreaControllers.length > 2 && (
                  <Button
                    onClick={handleBackToList}
                    sx={{
                      minWidth: 'auto',
                      textTransform: 'none',
                      p: 1,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Typography variant="h6">
                  Areas handled by {selectedController.firstName}{' '}
                  {selectedController.lastName}
                </Typography>
              </Box>
              <Button
                onClick={handleCloseModal}
                sx={{
                  minWidth: 'auto',
                  textTransform: 'none',
                  p: 1,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                Close
              </Button>
            </DialogTitle>
            <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', p: 3 }}>
              <List>
                {selectedController.areas.map((area) => (
                  <Paper
                    key={area.id}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                      '&:last-child': { mb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold' }}
                      >
                        {area.areaName}
                      </Typography>
                      <MuiChip
                        label={area.areaCode}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    {area.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {area.description}
                      </Typography>
                    )}

                    {area.parentArea && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <Box component="span" sx={{ fontWeight: 'medium' }}>
                          Parent Area:
                        </Box>{' '}
                        {area.parentArea}
                      </Typography>
                    )}

                    {area.provinceList.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Branches
                        </Typography>
                        <Grid container spacing={1}>
                          {area.provinceList.map((province) => (
                            <Grid item xs={6} key={province.id}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  bgcolor: 'background.default',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  {province.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {province.regionName}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Created: <DateFormatter dateString={area.dateCreated} />
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Modified:{' '}
                        <DateFormatter dateString={area.dateModified} />
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </List>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* View All ACs Modal */}
      <Dialog
        open={isViewAllModalOpen}
        onClose={() => setIsViewAllModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">All Area Controllers</Typography>
          <Button
            onClick={() => setIsViewAllModalOpen(false)}
            sx={{
              minWidth: 'auto',
              textTransform: 'none',
              p: 1,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', p: 3 }}>
          <List>
            {userAreaControllers.map((controller) => (
              <Paper
                key={controller.id}
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  '&:last-child': { mb: 0 },
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.04)',
                  },
                }}
                onClick={() => {
                  setIsViewAllModalOpen(false)
                  handleChipClick(controller)
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {controller.firstName} {controller.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {controller.areas.length} area
                  {controller.areas.length !== 1 ? 's' : ''} assigned
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {controller.areas.map((area) => (
                    <MuiChip
                      key={area.id}
                      label={area.areaName}
                      size="small"
                      sx={{ mr: 1, mt: 1 }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AreaControllerCollumnChips
